import { Application, Container } from "pixi.js";
import {
  RoomSection,
  RoomSectionBounds,
  RoomSectionConfig,
  PokemonWithSlot,
} from "./room-section";
import { RaidRoomOption } from "types/raids/rooms";
import { RaidPokemon, SlotConfig, PlayerRole } from "types/raid-dynamodb";

const EXPANSION_DURATION_MS = 800;
const TRANSLATE_DURATION_MS = 400;

/**
 * Manages the PIXI-side logic for room selection.
 * Creates 3 vertically-stacked sections, each showing a room option
 * with its map background and enemy Pokemon.
 */
export class RoomSelectionScene {
  private sections: RoomSection[] = [];
  private app: Application;
  private world: Container;
  private screenWidth: number;
  private screenHeight: number;
  private isDestroyed = false;
  private isExpanding = false;

  constructor(app: Application, world: Container) {
    this.app = app;
    this.world = world;
    this.screenWidth = app.screen.width;
    this.screenHeight = app.screen.height;
  }

  /**
   * Initialize the room selection scene with 3 room options
   */
  async initialize(roomOptions: RaidRoomOption[]): Promise<void> {
    if (this.isDestroyed) return;

    // Get fresh screen dimensions
    this.screenWidth = this.app.screen.width;
    this.screenHeight = this.app.screen.height;

    // Ensure we have exactly 3 options (take first 3 if more)
    const options = roomOptions.slice(0, 3);

    // Calculate section bounds (3 equal vertical sections)
    const sectionHeight = this.screenHeight / 3;

    // Config for battle-consistent positioning
    const config: RoomSectionConfig = {
      screenWidth: this.screenWidth,
      screenHeight: this.screenHeight,
    };

    for (let i = 0; i < options.length; i++) {
      const bounds: RoomSectionBounds = {
        x: 0,
        y: i * sectionHeight,
        width: this.screenWidth,
        height: sectionHeight,
      };

      const section = new RoomSection(this.app, options[i], i, bounds, config);

      this.sections.push(section);
      this.world.addChild(section.container);
    }

    // Load all maps and spawn enemies in parallel
    const loadPromises = this.sections.map(async (section) => {
      await section.loadMap();
      await section.spawnEnemies();
      // Apply mask after content is loaded
      section.applyMask();
    });

    await Promise.all(loadPromises);
  }

  /**
   * Get the section at a given index
   */
  getSection(index: number): RoomSection | undefined {
    return this.sections[index];
  }

  /**
   * Get all sections
   */
  getSections(): RoomSection[] {
    return this.sections;
  }

  /**
   * Get section bounds for positioning HTML overlay elements
   */
  getSectionBounds(): RoomSectionBounds[] {
    return this.sections.map((section) => section.bounds);
  }

  /**
   * Expand the selected room to fill the entire screen.
   * Animates the selected section while destroying others.
   *
   * @param selectedIndex Index of the selected room (0, 1, or 2)
   * @returns Promise that resolves when animation is complete
   */
  async expandRoom(selectedIndex: number): Promise<void> {
    if (this.isDestroyed || this.isExpanding) return;
    if (selectedIndex < 0 || selectedIndex >= this.sections.length) return;

    this.isExpanding = true;
    const selectedSection = this.sections[selectedIndex];

    // Step 1: If not top section, translate to top first
    if (selectedIndex > 0) {
      await this.translateSectionToTop(selectedSection, selectedIndex);
    }

    // Step 2: Destroy non-selected sections
    for (let i = 0; i < this.sections.length; i++) {
      if (i !== selectedIndex) {
        const section = this.sections[i];
        if (section.container && !section.container.destroyed) {
          this.world.removeChild(section.container);
        }
        section.destroy();
      }
    }

    // Step 3: Switch to battle positioning mode before expansion
    // This transitions the map from preview (top-aligned) to battle (grid-centered)
    selectedSection.switchToBattleMode();

    // Step 4: Expand selected section to full screen
    await this.expandSectionToFullScreen(selectedSection);

    // Step 5: Remove mask to show full map
    selectedSection.removeMask();

    this.isExpanding = false;
  }

  /**
   * Animate section translation to top position
   */
  private translateSectionToTop(
    section: RoomSection,
    fromIndex: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      // Guard against destroyed/null container
      if (!section.container || section.container.destroyed) {
        resolve();
        return;
      }

      const startY = section.container.y;
      const targetY = 0;
      const startTime = performance.now();

      const animate = () => {
        if (
          this.isDestroyed ||
          !section.container ||
          section.container.destroyed
        ) {
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(1, elapsed / TRANSLATE_DURATION_MS);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);

        section.container.y = startY + (targetY - startY) * eased;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          section.bounds.y = targetY;
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Animate section expansion to fill entire screen
   */
  private expandSectionToFullScreen(section: RoomSection): Promise<void> {
    return new Promise((resolve) => {
      const startHeight = section.bounds.height;
      const targetHeight = this.screenHeight;
      const startTime = performance.now();

      const animate = () => {
        if (
          this.isDestroyed ||
          !section.container ||
          section.container.destroyed
        ) {
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(1, elapsed / EXPANSION_DURATION_MS);

        // Ease out cubic for smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 3);

        const currentHeight =
          startHeight + (targetHeight - startHeight) * eased;

        section.updateBounds({
          x: 0,
          y: 0,
          width: this.screenWidth,
          height: currentHeight,
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    for (const section of this.sections) {
      this.world.removeChild(section.container);
      section.destroy();
    }
    this.sections = [];
  }

  /**
   * Get the selected section after expansion (the one that wasn't destroyed)
   */
  getExpandedSection(): RoomSection | null {
    return this.sections.find((s) => s.pokemons.length > 0) || null;
  }

  /**
   * Update player pokemon on the expanded section.
   * Call this when player selections change during draft phase.
   * Uses incremental updates - only adds/removes changed pokemon.
   *
   * @param pokemonsWithSlots Array of pokemon with their slot indices
   * @param totalSlots Total number of slots for positioning
   */
  async updatePlayerPokemonWithSlots(
    pokemonsWithSlots: PokemonWithSlot[],
    totalSlots: number,
  ): Promise<void> {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    await expandedSection.updatePlayerPokemon(pokemonsWithSlots, totalSlots);
  }

  /**
   * Legacy method - updates player pokemon without slot info.
   * @deprecated Use updatePlayerPokemonWithSlots instead
   */
  async updatePlayerPokemon(pokemons: RaidPokemon[]): Promise<void> {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    await expandedSection.spawnPlayerPokemon(pokemons);
  }

  /**
   * Clear all player pokemon from the expanded section.
   */
  clearPlayerPokemon(): void {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    expandedSection.clearPlayerPokemon();
  }

  /**
   * Update slot placeholders on the expanded section.
   * Shows ellipses with player names / "SHARED" at spawn positions.
   */
  updateSlotPlaceholders(
    slots: SlotConfig[],
    players: Record<PlayerRole, { username: string } | null>,
  ): void {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    expandedSection.updateSlotPlaceholders(slots, players);
  }

  /**
   * Apply item effect visual feedback to a pokemon.
   * Shows healing VFX, floating text, item icon, and syncs HP bar.
   *
   * @param targetPokemonId - The pokemon ID to apply the effect to
   * @param itemName - The item name (e.g., "potion", "super-potion")
   * @param healAmount - The amount healed (for floating text)
   * @param newHp - The new HP value after healing (for HP bar sync)
   */
  async applyItemEffect(
    targetPokemonId: number,
    itemName: string,
    healAmount: number,
    newHp: number,
  ): Promise<void> {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    await expandedSection.applyItemEffect(
      targetPokemonId,
      itemName,
      healAmount,
      newHp,
    );
  }

  /**
   * Show targeting indicators on valid pokemon for item usage.
   * @param validTargetIds Array of pokemon IDs that can be targeted
   */
  showTargetingIndicators(validTargetIds: number[]): void {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    expandedSection.showTargetingIndicators(validTargetIds);
  }

  /**
   * Clear all targeting indicators
   */
  clearTargetingIndicators(): void {
    if (this.isDestroyed) return;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return;

    expandedSection.clearTargetingIndicators();
  }

  /**
   * Get pokemon ID at screen position (for click/drop detection)
   * @param screenX Screen X coordinate
   * @param screenY Screen Y coordinate
   * @param validTargetIds Only consider these pokemon IDs
   * @returns Pokemon ID if found, null otherwise
   */
  getPokemonAtPosition(
    screenX: number,
    screenY: number,
    validTargetIds: number[],
  ): number | null {
    if (this.isDestroyed) return null;

    const expandedSection = this.getExpandedSection();
    if (!expandedSection) return null;

    return expandedSection.getPokemonAtPosition(screenX, screenY, validTargetIds);
  }
}
